import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * Shopping-session state shared across every purchase surface (Store,
 * Cart drawer, Track page, Catalog list, Genre / Section pages, Bundle).
 *
 * The core problem this fixes: each surface previously kept its OWN
 * `termsAccepted` boolean, so accepting the terms on the Store card and
 * then hitting Checkout in the drawer would show a false "please accept
 * the terms" error even though the customer HAD accepted. Now there is
 * exactly one authoritative source.
 *
 * Scope: session-level. We use `sessionStorage` so the acceptance:
 *   * carries across in-app navigation (react-router pushes, tab
 *     switches within the SPA);
 *   * carries across a reload of the SPA in the same tab;
 *   * does NOT leak into an unrelated future browser session — closing
 *     the tab clears it. Nothing here is written to `localStorage`.
 *
 * Version-gated: if the site changes its `policyVersion` in the future,
 * any stored acceptance for the old version is ignored so we never mark
 * a customer as having agreed to terms they haven't seen.
 */
const STORAGE_KEY = "djspy_shopping_session";
const CURRENT_POLICY_VERSION = "2026-01-11";

const ShoppingSessionContext = createContext(null);

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.policyVersion !== CURRENT_POLICY_VERSION) return null;
    return parsed;
  } catch { return null; }
}

function writeStored(next) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); }
  catch { /* private mode / quota — non-fatal */ }
}

export function ShoppingSessionProvider({ children }) {
  const [termsAccepted, setTermsAcceptedRaw] = useState(() => Boolean(readStored()?.termsAccepted));
  // `nudged` toggles a temporary "please accept the terms first" state
  // that any TermsBlock can render as a persistent inline warning next
  // to its checkbox. Cleared automatically as soon as the customer
  // ticks the box, but visible until they do.
  const [nudged, setNudged] = useState(false);
  const anchorsRef = useRef(new Set());

  const setTermsAccepted = useCallback((v) => {
    const next = Boolean(v);
    setTermsAcceptedRaw(next);
    writeStored({ termsAccepted: next, policyVersion: CURRENT_POLICY_VERSION });
    if (next) setNudged(false);
  }, []);

  const registerTermsAnchor = useCallback((el) => {
    if (!el) return () => {};
    anchorsRef.current.add(el);
    return () => { anchorsRef.current.delete(el); };
  }, []);

  const nudgeTerms = useCallback(() => {
    setNudged(true);
    // Scroll to and focus the closest visible Terms control. Prefer the
    // anchor that is currently onscreen or nearest to viewport top.
    try {
      const anchors = Array.from(anchorsRef.current).filter((el) => el?.isConnected);
      const visible = anchors.find((el) => {
        const r = el.getBoundingClientRect();
        return r.top >= 0 && r.top < window.innerHeight;
      }) || anchors[0];
      if (visible) {
        visible.scrollIntoView({ behavior: "smooth", block: "center" });
        const cb = visible.querySelector('input[type="checkbox"]');
        if (cb && typeof cb.focus === "function") {
          setTimeout(() => cb.focus(), 250);
        }
      }
    } catch { /* noop */ }
  }, []);

  // Sync sessionStorage → state if another tab/page changed it (rare but
  // possible when the SPA is opened in two tabs).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        const stored = readStored();
        setTermsAcceptedRaw(Boolean(stored?.termsAccepted));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({
    termsAccepted,
    setTermsAccepted,
    nudgeTerms,
    nudged,
    registerTermsAnchor,
    policyVersion: CURRENT_POLICY_VERSION,
  }), [termsAccepted, setTermsAccepted, nudgeTerms, nudged, registerTermsAnchor]);

  return (
    <ShoppingSessionContext.Provider value={value}>
      {children}
    </ShoppingSessionContext.Provider>
  );
}

export function useShoppingSession() {
  const ctx = useContext(ShoppingSessionContext);
  if (!ctx) {
    // Defensive noop for standalone tests. In the app the provider is
    // mounted at the App root so this branch is unreachable.
    return {
      termsAccepted: false,
      setTermsAccepted: () => {},
      nudgeTerms: () => {},
      nudged: false,
      registerTermsAnchor: () => () => {},
      policyVersion: CURRENT_POLICY_VERSION,
    };
  }
  return ctx;
}
